import React from 'react';
import { Input } from '@/components/ui/input';
import {
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import type { AutocompleteSuggestionItem } from '@/hooks/useAutocomplete';

/**
 * Props for the AutocompleteInput component
 */
interface AutocompleteInputProps<T extends AutocompleteSuggestionItem> {
  /** The input field name (used for form field identification and accessibility) */
  name: string;
  /** The current input value */
  value: string;
  /** Callback when the input value changes */
  onChange: (value: string) => void;
  /** Callback when the input loses focus */
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Callback when the input gains focus */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Callback for keyboard events (used for suggestion navigation) */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Label text displayed above the input */
  label: string;
  /** Optional description text displayed below the input */
  description?: string;
  /** Array of autocomplete suggestions to display in dropdown */
  suggestions: T[];
  /** Whether suggestions are currently being fetched from API */
  isFetching: boolean;
  /** Index of the currently focused suggestion (-1 if none focused) */
  focusedIndex: number;
  /** Callback when a suggestion is clicked */
  onSuggestionClick: (suggestion: T) => void;
  /** Optional function to render custom suggestion content */
  renderSuggestion?: (suggestion: T, query: string) => React.ReactNode;
  /** Minimum number of characters that must match potential suggestions to show dropdown. Defaults to 0. */
  minMatchingLength?: number;
}

/**
 * Default suggestion renderer for AutocompleteSuggestionItem
 */
function DefaultSuggestionRenderer({
  suggestion,
  query,
}: {
  suggestion: AutocompleteSuggestionItem;
  query: string;
}) {
  // If no query, just display the text without highlighting
  if (!query.trim()) {
    return <span>{suggestion.suggestion}</span>;
  }

  // Create a regex to find matches, escaping special regex characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = suggestion.suggestion.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span
            key={i}
            className='bg-primary/10 text-primary font-medium px-0.5 rounded-sm'
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

/**
 * A hybrid autocomplete input component that combines direct text input with professional dropdown styling.
 *
 * This component provides an enhanced autocomplete experience by:
 * - Using a regular Input for direct typing (maintaining natural UX)
 * - Using absolutely positioned dropdown for professional appearance
 * - Supporting custom suggestion rendering
 * - Keeping the input visible and focused while suggestions are shown
 * - Supporting dynamic API-driven suggestions with loading states
 * - Integrating seamlessly with form validation
 * - Manual keyboard navigation that doesn't interfere with typing
 *
 * **Key Features:**
 * - **Direct typing UX**: Users can type directly into the input field
 * - **Custom rendering**: Supports custom suggestion display logic
 * - **Professional appearance**: Auto-sized dropdown with shadcn/ui styling
 * - **Manual keyboard navigation**: Arrow keys, Enter, Escape work without blocking typing
 * - **Dynamic suggestions**: Supports real-time API calls as user types
 * - **Loading states**: Shows spinner during API calls
 * - **Input stays focused**: User can continue typing while suggestions are shown
 *
 * @param props - All required props for the autocomplete input
 * @returns JSX element containing the input with dropdown suggestions
 */
export function AutocompleteInput<T extends AutocompleteSuggestionItem>({
  name,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  placeholder,
  label,
  description,
  suggestions,
  isFetching,
  focusedIndex,
  onSuggestionClick,
  renderSuggestion,
  minMatchingLength = 0,
}: AutocompleteInputProps<T>) {
  // Keep track of the last valid suggestions to show while fetching
  const [cachedSuggestions, setCachedSuggestions] = React.useState<T[]>([]);

  // Update cached suggestions when we get new ones and we're not fetching
  React.useEffect(() => {
    if (!isFetching && suggestions.length > 0) {
      setCachedSuggestions(suggestions);
    }
  }, [suggestions, isFetching]);

  // Clear cached suggestions when input becomes empty to prevent memory leaks
  React.useEffect(() => {
    if (!value.trim()) {
      setCachedSuggestions([]);
    }
  }, [value]);

  // Memoize the filter function to prevent unnecessary re-computations
  const filterSuggestions = React.useCallback(
    (suggestionsToFilter: T[], inputValue: string) => {
      if (minMatchingLength === 0) {
        return suggestionsToFilter;
      }

      // If input is empty or shorter than minimum length, don't show suggestions
      if (!inputValue.trim() || inputValue.trim().length < minMatchingLength) {
        return [];
      }

      // Filter suggestions that match the input (case-insensitive substring matching)
      return suggestionsToFilter.filter((suggestion) => {
        const suggestionText = suggestion.suggestion.toLowerCase();
        const inputText = inputValue.toLowerCase();
        return suggestionText.includes(inputText);
      });
    },
    [minMatchingLength]
  );

  // Filter suggestions based on minimum matching length
  const filteredSuggestions = React.useMemo(() => {
    // Use cached suggestions if we're fetching and have them, otherwise use current suggestions
    const suggestionsToFilter =
      isFetching && cachedSuggestions.length > 0
        ? cachedSuggestions
        : suggestions;

    return filterSuggestions(suggestionsToFilter, value);
  }, [suggestions, cachedSuggestions, value, isFetching, filterSuggestions]);

  // Control dropdown visibility based on filtered suggestions, fetching with previous suggestions, or fetching with no suggestions (first fetch)
  const isOpen =
    filteredSuggestions.length > 0 ||
    (isFetching && cachedSuggestions.length > 0) ||
    (isFetching && suggestions.length === 0);

  return (
    <FormItem>
      <FormLabel>{label}</FormLabel>
      <div className='relative'>
        {/* INPUT - basic and working, now with keyboard navigation */}
        <Input
          id={name}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          style={{ textAlign: 'left' }}
          className='text-left'
          // Basic ARIA attributes for accessibility
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          aria-controls={isOpen ? `${name}-listbox` : undefined}
        />

        {/* DROPDOWN - auto-sized width with improved theming */}
        {isOpen && (
          <div
            id={`${name}-listbox`}
            className='absolute top-full left-0 z-50 mt-1 bg-popover border border-border rounded-md shadow-md max-h-[300px] overflow-y-auto w-fit'
            role='listbox'
            aria-label={`${label} suggestions`}
            aria-expanded={true}
          >
            {/* Show loading indicator only when no previous suggestions exist */}
            {isFetching &&
              filteredSuggestions.length === 0 &&
              suggestions.length === 0 &&
              (minMatchingLength === 0 ||
                value.trim().length >= minMatchingLength) && (
                <div className='flex items-center py-3 px-4'>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  <span className='text-muted-foreground'>
                    Fetching suggestions...
                  </span>
                </div>
              )}

            {/* Show subtle loading indicator when fetching with existing suggestions */}
            {isFetching && suggestions.length > 0 && (
              <div className='absolute top-2 right-2'>
                <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />
              </div>
            )}

            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.suggestion}-${index}`}
                role='option'
                tabIndex={0}
                aria-selected={focusedIndex === index}
                className={`cursor-pointer py-3 px-4 border-b border-border/50 last:border-b-0 whitespace-nowrap ${
                  focusedIndex === index
                    ? 'bg-secondary text-secondary-foreground'
                    : 'hover:bg-secondary/50 hover:text-secondary-foreground'
                }`}
                onClick={() => onSuggestionClick(suggestion)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSuggestionClick(suggestion);
                  }
                }}
              >
                {renderSuggestion ? (
                  renderSuggestion(suggestion, value)
                ) : (
                  <DefaultSuggestionRenderer
                    suggestion={suggestion}
                    query={value}
                  />
                )}
              </div>
            ))}

            {!isFetching &&
              filteredSuggestions.length === 0 &&
              value.trim() && (
                <div className='py-3 px-4 whitespace-nowrap'>
                  <span className='text-muted-foreground'>
                    No results found.
                  </span>
                </div>
              )}
          </div>
        )}
      </div>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
}
